import NotFoundPage from 'src/pages/not-found';
import { useMemo } from 'react';
import { VendorGetServerSideProps } from 'vendor/utils/VendorGetServerSideProps';
import { configureSSR } from 'src/utils/configureSSR';
import { fetchProductDetailsQuery } from 'src/modules/products/hooks/useProductDetailsQuery';
import {
  getProductCategory,
  isProtocol,
  ProductCategoryCode,
  ProductDTO,
} from 'src/models/pim';
import { ProductPageDevicesTemplate } from 'src/modules/products/templates/ProductPageDevicesTemplate';
import { ProductPageFillersTemplate } from 'src/modules/products/templates/ProductPageFillersTemplate';
import { ProductPageCosmeceuticalsTemplate } from 'src/modules/products/templates/ProductPageCosmeceuticalsTemplate';

const productCategoryTemplateMap: Record<
  ProductCategoryCode,
  (product: ProductDTO) => JSX.Element
> = {
  [ProductCategoryCode.Cosmeceuticals]: ProductPageCosmeceuticalsTemplate,
  [ProductCategoryCode.Devices]: ProductPageDevicesTemplate,
  [ProductCategoryCode.Fillers]: ProductPageFillersTemplate,
};

const ProductPage = ({ product }: { product: ProductDTO }) => {
  const category = getProductCategory(product);

  const Renderer = useMemo(
    () => productCategoryTemplateMap[category],
    [category],
  );

  if (Renderer) {
    return <Renderer {...product} />;
  }

  return <NotFoundPage />;
};

export const getServerSideProps = VendorGetServerSideProps(async (ctx) => {
  const code = String(ctx.params?.code ?? ctx.params?.categoryOrCode);

  const query = fetchProductDetailsQuery(ctx.apis, code);

  const product = await ctx.queryClient.fetchQuery(query);

  if (!product) {
    return {
      notFound: true,
    };
  }

  if (isProtocol(product)) {
    return {
      redirect: {
        destination: `/protocols/${product.code}`,
        permanent: false,
      },
    };
  }

  return configureSSR(ctx, {
    layout: {
      variant: 'default',
      tvbar: false,
    },
    cacheControl: {
      maxAge: 600,
      sMaxAge: 1200,
    },
    actions: {
      common: () => [ctx.queryClient.setQueryData(query.queryKey, product)],
    },
    return: {
      props: { product },
    },
  });
});

export default ProductPage;
